<template>
	<v-card>
		<v-card-text>
			<div class="d-flex flex-row">
				<div class="d-flex flex-column w-25">
					<v-card variant="flat" title="Search Stations">
						<v-card-text>
							<v-text-field v-model="searchField"
								variant="outlined"
								placeholder="Search Stations"
								clearable
							/>

							<div class="d-flex justify-center">
								<v-btn-toggle
									v-model="buttonToggle"
									color="secondary"
									variant="outlined"
									density="compact"
									mandatory
									divided
								>
									<v-btn prepend-icon="mdi-web">
										All
									</v-btn>
									<v-btn prepend-icon="mdi-check-circle">
										Active Only
									</v-btn>
									<v-btn prepend-icon="mdi-circle-outline">
										Inactive Only
									</v-btn>
								</v-btn-toggle>
							</div>

							<div class="d-inline-flex align-center justify-space-between w-100">
								<v-checkbox v-model="selectAll"
									label="Select All"
									density="compact"
									hide-details
									@update:model-value="handleSelectAll"
								/>

								<v-btn color="secondary" @click="resetStations">
									Reset
								</v-btn>
							</div>

							<perfect-scrollbar style="max-height: calc(100vh - 178px)">
								<v-list v-model:selected="assetStationsSelection"
									select-strategy="leaf"
								>
									<v-list-item v-for="station in assetStationsList" :key="station.StationID"
										:title="station.StationName"
										:value="station.StationID"
										:active="false"
										border="sm"
										@click.prevent="handleChangeSelectedStation(station)"
									>
										<template v-slot:prepend>
											<v-checkbox-btn :model-value="getCheckboxEnabled(station.StationID)"
												@click="setCheckboxEnabled(station.StationID)"/>
										</template>
										<template v-slot:append>
											<div class="d-inline-flex align-center">
												<v-icon v-if="hasSpecificMarkers(station.StationID)" class="me-5"
													icon="mdi-square-wave" color="secondary" size="small"/>
												<v-badge v-if="usesGSelector" content="GS" color="orange"/>
											</div>
										</template>
									</v-list-item>
								</v-list>
							</perfect-scrollbar>
							<div v-if="loading" class="d-flex">
								<Loader :value="loading"/>
							</div>
						</v-card-text>
					</v-card>
				</div>
				<v-divider vertical/>
				<div class="d-flex flex-column w-75">
					<v-card variant="outlined">
						<v-card-text>
							<v-form ref="asset-management-station-form" v-model="valid">
								<div class="d-inline-flex w-100">
									<div class="d-flex flex-column w-100 mx-2">
										<label class="text-caption"> Asset ID </label>
										<v-text-field v-model="formData.AssetID"
											variant="outlined"
											density="compact"
											disabled
										/>
									</div>
									<div class="d-flex flex-column w-100 mx-2">
										<label class="text-caption"> Title </label>
										<v-text-field v-model="formData.Title"
											variant="outlined"
											density="compact"
											disabled
										/>
									</div>
									<div class="d-flex flex-column w-100 mx-2">
										<label class="text-caption"> Album </label>
										<v-text-field v-model="formData.Album"
											variant="outlined"
											density="compact"
											disabled
										/>
									</div>
									<div class="d-flex flex-column w-100 mx-2">
										<label class="text-caption"> Artist </label>
										<v-text-field v-model="formData.Artist"
											variant="outlined"
											density="compact"
											disabled
										/>
									</div>
									<div class="d-flex flex-column w-100 mx-2">
										<label class="text-caption"> Voice </label>
										<v-text-field v-model="formData.Voice"
											placeholder="Voice"
											variant="outlined"
											density="compact"
											disabled
										/>
									</div>
								</div>
								<div class="d-inline-flex w-100">
									<div class="d-flex flex-column w-100 mx-2">
										<label class="text-caption"> Duration </label>
										<v-text-field :model-value="secondsToFormat(formData.Duration, 'mm:ss')"
											variant="outlined"
											density="compact"
											readonly
										/>
									</div>
									<div class="d-flex flex-column w-100 mx-2">
										<label class="text-caption"> Length </label>
										<v-text-field :model-value="secondsToFormat(formData.Length, 'mm:ss')"
											variant="outlined"
											density="compact"
											readonly
										/>
									</div>
								</div>
							</v-form>

							<v-card class="my-4"
								:title="stationTitle"
								:color="stationIsActive ? 'secondary' : 'error'"
								variant="flat">
							</v-card>

							<v-tabs v-model="activeTab">
								<v-tab
									v-for="(tab, index) in tabItems"
									:key="index"
									:text="tab"
									:value="index"
									variant="text"
									color="secondary"
									density="compact"
									:disabled="!selectedStation"
								></v-tab>
							</v-tabs>
							<AssetManagementStationMarksTab v-if="activeTab === 0 && !loading"
								@override:station-marker="handleOverrideStationMarker"
							/>
							<AssetManagementStationDataTab v-else-if="activeTab === 1 && !loading"/>
							<div v-if="loading" class="d-flex">
								<Loader :value="loading"/>
							</div>
						</v-card-text>
					</v-card>
				</div>
			</div>
		</v-card-text>
	</v-card>
</template>

<script>
/* Mixins */
import DurationFormatMixin from "@/mixins/DurationFormatMixin";

/* Modules */
import AssetManagementStationMarksTab from "@/components/standaloneModules/AssetManagementStationMarksTab.vue";
import AssetManagementStationDataTab from "@/components/standaloneModules/AssetManagementStationDataTab.vue";
import Loader from "@/components/Loader.vue";

/* Libraries */
import emitter from "@/emitter";
import _ from "lodash";

export default {
	name: "AssetManagementStationTab",

	components: {AssetManagementStationDataTab, AssetManagementStationMarksTab, Loader},

	mixins: [DurationFormatMixin],

	data() {
		return {
			activeTab: 0,
			tabItems: ["Station Marks", "Station Data"],
			searchField: null,
			selectAll: true,
			buttonToggle: 1,
			assetStationsList: [],
			assetStationsSelection: [],
			initialActiveStations: [],
			formData: {},
			valid: true,
			loading: true,
			unsavedChanges: 0
		}
	},

	computed: {
		event() {
			return this.$store.getters.getAssetManagementEvent();
		},

		asset() {
			return this.$store.getters.getAssetManagementAsset() ?? this.event?.Asset;
		},

		selectedStation() {
			return this.$store.getters.getAssetManagementSelectedStation();
		},

		stationIsActive() {
			return this.activeStationsList.some(as => as.StationID === this.selectedStation?.StationID);
		},

		stationTitle() {
			return this.selectedStation ?
				   this.stationIsActive ?
				   this.selectedStation?.StationName :
				   `${this.selectedStation?.StationName} [Asset is inactive]` :
				   "No station selected";
		},

		allStationsList() {
			return this.$store.getters.getAssetManagementAllStationsList();
		},

		activeStationsList() {
			return this.$store.getters.getAssetManagementActiveStationsList();
		},

		inactiveStationsList() {
			return _.differenceBy(this.allStationsList, this.activeStationsList, "StationID")
		},

		stationSpecificMarkersList() {
			return this.$store.getters.getAssetManagementSelectedStationMarkersList();
		}
	},

	methods: {
		initFormData() {
			this.formData = {
				AssetID: this.asset?.AssetID,
				Title: this.asset?.Title,
				Album: this.asset?.Album,
				Artist: this.asset?.Artist,
				Voice: this.asset?.Voice,
				Duration: this.asset?.DurationToSegue ?? this.asset?.POIMarkup?.Length ?? 0,
				Length: this.asset?.POIMarkup?.Length ?? 0
			}
		},

		updateAsset() {
			if (this.unsavedChanges === 0) {
				return;
			}
			this.loading = true;
			this.$store.dispatch("updateAssetStations", {
					AssetID: this.asset.AssetID,
					payload: this.allStationsList.reduce((acc, station) => {
						acc.push({
							Active: this.getCheckboxEnabled(station.StationID),
							StationID: station.StationID,
							AssetID: this.asset.AssetID,
						});
						return acc;
					}, [])
				})
				.then(() => {
					this.updateAssetStations();
				})
				.catch((err) => {
					console.log(err);
					this.loading = false;
				})
		},

		updateAssetStations() {
			this.$store.dispatch("getAssetAllStations", this.asset.AssetID)
				.then(assetStationsRes => {
					const activeList = assetStationsRes.data.reduce((acc, station) => {
						if (station.Active) {
							const StationName = this.allStationsList.find(s => s.StationID ===
																			   station.StationID)?.StationName;

							if (StationName) {
								if (station.POIMarkup) {
									this.$store.commit("setAssetManagementSelectedStationMarkers", {
										stationID: station.StationID,
										stationName: StationName,
										markers: station.POIMarkup
									});
								}
								acc.push({...station, StationName});
							}
						}
						return acc;
					}, []);

					this.$store.commit("setAssetManagementActiveStationsList", activeList);

					if (this.buttonToggle === 1) {
						this.assetStationsList = this.activeStationsList;
					}
					this.assetStationsSelection = this.activeStationsList.map(as => as.StationID);
					this.loading = false;
				});
		},

		handleAssetStationsList(mode) {
			if (mode === 0) {
				return this.allStationsList;
			}
			else if (mode === 1) {
				return this.activeStationsList;
			}
			else if (mode === 2) {
				return this.inactiveStationsList;
			}
		},

		filterAssetStationsList(filter) {
			if (filter) {
				let searchList = this.handleAssetStationsList(this.buttonToggle);
				this.assetStationsList = searchList.filter(station => station.StationName.toLowerCase()
																			 .includes(filter));
			}
			else {
				this.assetStationsList = this.handleAssetStationsList(this.buttonToggle);
			}
		},

		getCheckboxEnabled(stationID) {
			return !!this.activeStationsList.find(s => s.StationID === stationID);
		},

		setCheckboxEnabled(stationID) {
			let targetStationIndex = this.activeStationsList.findIndex(s => s.StationID === stationID);
			if (targetStationIndex > -1) {
				const tempArray = this.activeStationsList.filter((_, i) => i !== targetStationIndex);
				this.$store.commit("setAssetManagementActiveStationsList", tempArray);
				this.unsavedChanges--;
			}
			else {
				this.$store.commit(
					"setAssetManagementActiveStationsList",
					[...this.activeStationsList, this.allStationsList.find(s => s.StationID === stationID)]
				);
				this.unsavedChanges++;
			}
		},

		handleChangeSelectedStation(station) {
			this.$store.dispatch("getStation", station)
				.then(station => this.$store.commit("setAssetManagementSelectedStation", station));
		},

		handleOverrideStationMarker(stationID) {
			let station = this.assetStationsList.find(s => s.StationID === stationID);
			this.handleChangeSelectedStation(station);
		},

		handleSelectAll() {
			if (this.selectAll) {
				this.$store.commit(
					"setAssetManagementActiveStationsList",
					[...this.activeStationsList, ...this.inactiveStationsList]
				);
			}
			else {
				this.$store.commit("setAssetManagementActiveStationsList", []);
			}
		},

		resetStations() {
			if (!_.isEqual(this.activeStationsList, this.initialActiveStations)) {
				this.$store.commit("setAssetManagementActiveStationsList", this.initialActiveStations);
			}
		},

		hasSpecificMarkers(stationID) {
			return !!this.stationSpecificMarkersList.find(station => station.stationID === stationID)
		},

		usesGSelector(stationID) {
			return this.$store.dispatch("getStation", stationID)
					   .then(station => !!(station.GSelectorSettings?.ServiceAddress ||
										   station.GSelectorSettings?.SecondaryServiceAddress))
		}
	},

	watch: {
		asset() {
			this.initFormData();
		},

		searchField(searchValue) {
			this.filterAssetStationsList(searchValue);
		},

		selectAll(_, prev) {
			if (prev) {
				this.$store.commit(
					"setAssetManagementActiveStationsList",
					[...this.activeStationsList, ...this.inactiveStationsList]
				);
			}
		},

		buttonToggle(curr) {
			this.assetStationsList = this.handleAssetStationsList(curr);
			if (this.searchField) {
				this.filterAssetStationsList(this.searchField);
			}
		}
	},

	mounted() {
		const station = this.$store.getters.getLoadedStationById(this.$store.getters.assetManagementStationID());
		this.$store.commit("setAssetManagementSelectedStation", station);
		this.$store.dispatch("getStationsByOrgID")
			.then(stationRes => {
				this.$store.commit("setAssetManagementAllStationsList", stationRes.data);
				this.updateAssetStations();
				this.initialActiveStations = [...this.activeStationsList];
			});

		this.initFormData();
		emitter.on("saveAsset", this.updateAsset);
	},

	beforeUnmount() {
		emitter.off("saveAsset", this.updateAsset);
	}
}
</script>

<style scoped lang="scss">

</style>